import React, { useEffect, useRef, useState } from "react";
import api, { mediaUrl } from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, Label } from "@/admin/ui";
import { toast } from "sonner";
import { Trash2, Upload, Play, Pause, Loader2 } from "lucide-react";

const SoundAdmin = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const vidRef = useRef();
  const vidTarget = useRef(null);
  const audioRef = useRef();
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => { api.get("/admin/sound").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-espresso/50">Loading…</p>;
  const up = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const tracks = s.tracks || [];

  const save = async (next) => {
    const payload = next || s;
    try { const { data } = await api.put("/admin/sound", payload); setS(data); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const track = { id: crypto.randomUUID(), title: file.name.replace(/\.[^.]+$/, ""), file_url: data.url };
      const next = { ...s, tracks: [...tracks, track], active_track_id: s.active_track_id || track.id };
      setS(next); await save(next);
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };

  const delTrack = async (id) => {
    const next = { ...s, tracks: tracks.filter((t) => t.id !== id), active_track_id: s.active_track_id === id ? "" : s.active_track_id };
    setS(next); await save(next);
  };

  const uploadVideo = async (file) => {
    if (!file || !vidTarget.current) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const id = vidTarget.current;
      const next = { ...s, tracks: tracks.map((x) => x.id === id ? { ...x, video_url: data.url } : x) };
      setS(next); await save(next);
      toast.success("Video paired to song");
    } catch { toast.error("Video upload failed"); }
    setBusy(false);
  };

  const preview = (t) => {
    if (previewing === t.id) { audioRef.current?.pause(); setPreviewing(null); return; }
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = new Audio(mediaUrl(t.file_url));
    audioRef.current.play(); setPreviewing(t.id);
    audioRef.current.onended = () => setPreviewing(null);
  };

  return (
    <div className="max-w-2xl">
      <PageHead title="Sound" sub="The NoirbyAmara soundscape.">
        <Btn onClick={() => save()} testid="admin-save">Save</Btn>
      </PageHead>

      <Card className="mb-4">
        <Toggle label="Enable site sound" value={s.enabled} onChange={(v) => { const n = { ...s, enabled: v }; setS(n); save(n); }} />
        <Toggle label="Loop" value={s.loop} onChange={(v) => up("loop", v)} />
        <Label>Default volume — {Math.round((s.default_volume ?? 0.4) * 100)}%</Label>
        <input type="range" min="0" max="1" step="0.05" value={s.default_volume ?? 0.4} onChange={(e) => up("default_volume", parseFloat(e.target.value))} className="w-full accent-sage" />
      </Card>

      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-serif text-2xl">Tracks</h2>
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="flex items-center gap-2 text-[0.66rem] tracking-widest uppercase border border-espresso px-3 py-2 hover:bg-espresso hover:text-cream transition-colors">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Track
          </button>
          <input ref={fileRef} type="file" accept="audio/*" hidden onChange={(e) => upload(e.target.files[0])} />
        </div>
        {tracks.length === 0 && <p className="text-sm text-espresso/40">No tracks yet. Upload an audio file you are authorised to use.</p>}
        {tracks.map((t) => (
          <div key={t.id} className="border border-espresso/15 px-3 py-2.5 mb-2">
            <div className="flex items-center gap-3">
              <button onClick={() => preview(t)} className="w-8 h-8 rounded-full border border-espresso flex items-center justify-center shrink-0">
                {previewing === t.id ? <Pause size={13} /> : <Play size={13} />}
              </button>
              <input value={t.title} onChange={(e) => up("tracks", tracks.map((x) => x.id === t.id ? { ...x, title: e.target.value } : x))}
                className="flex-1 bg-ivory border border-espresso/20 px-3 py-1.5 text-sm" />
              <label className="flex items-center gap-1.5 text-[0.64rem] tracking-widest uppercase shrink-0">
                <input type="radio" name="active" checked={s.active_track_id === t.id} onChange={() => { const n = { ...s, active_track_id: t.id }; setS(n); save(n); }} /> Starts
              </label>
              <button onClick={() => delTrack(t.id)} className="text-wine shrink-0"><Trash2 size={16} /></button>
            </div>
            <div className="flex items-center gap-3 mt-2.5">
              <div className="w-12 h-14 bg-cocoa overflow-hidden flex items-center justify-center shrink-0">
                {t.video_url ? <video src={mediaUrl(t.video_url)} muted loop autoPlay playsInline className="w-full h-full object-cover" /> : <span className="text-cream/40 text-[0.52rem] text-center px-1">No video</span>}
              </div>
              <input value={t.video_url || ""} onChange={(e) => up("tracks", tracks.map((x) => x.id === t.id ? { ...x, video_url: e.target.value } : x))}
                placeholder="Paired video — the visual that matches this song's vibe"
                className="flex-1 bg-ivory border border-espresso/20 px-3 py-1.5 text-sm" />
              <button onClick={() => { vidTarget.current = t.id; vidRef.current?.click(); }} disabled={busy}
                className="flex items-center gap-1.5 text-[0.62rem] tracking-widest uppercase border border-espresso px-2.5 py-1.5 hover:bg-espresso hover:text-cream transition-colors shrink-0">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Video
              </button>
            </div>
          </div>
        ))}
        <input ref={vidRef} type="file" accept="video/*" hidden onChange={(e) => uploadVideo(e.target.files[0])} />
        <p className="text-[0.62rem] text-espresso/45 mt-2">Songs play in order and loop; the homepage hero shows each song's paired video as it plays. "Starts" marks the opening song. Upload your own songs & videos to stay copyright-clear.</p>
      </Card>
    </div>
  );
};

export default SoundAdmin;
